/**
 * CLI + SDKs Getting Started page.
 * Interactive documentation for CLI and SDK installation and usage.
 *
 * @module pages/GettingStartedPage
 */

import { useState } from 'react';
import { Copy, Download, Terminal, Code, ExternalLink, CheckCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { PageHeader } from '@/components/layout';
import { Card, CardHeader, CardBody } from '@/components/ui';
import { clsx } from 'clsx';

type SdkType = 'cli' | 'python' | 'typescript';

const RELEASE_URL = 'https://msft.ghe.com/coreai/adc/releases/latest';

/**
 * Code block with copy functionality
 */
function CodeBlock({
  code,
  language = 'bash',
  title,
}: {
  code: string;
  language?: string;
  title?: string;
}) {
  const copyToClipboard = () => {
    navigator.clipboard.writeText(code);
    toast.success('Copied to clipboard');
  };

  return (
    <div className="relative group">
      {title && (
        <div className="px-4 py-2 bg-gray-100 dark:bg-[#1a1a1a] border-b border-gray-200 dark:border-[#2a2a2a] rounded-t-lg">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{title}</span>
        </div>
      )}
      <pre
        tabIndex={0}
        role="region"
        aria-label={title ? `${title} code` : `${language} code`}
        className={clsx(
          'bg-gray-50 dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#2a2a2a] p-4 text-sm overflow-x-auto text-gray-800 dark:text-gray-200 font-mono',
          title ? 'rounded-b-lg' : 'rounded-lg'
        )}
      >
        <code className={`language-${language}`}>{code}</code>
      </pre>
      <button
        type="button"
        onClick={copyToClipboard}
        className="absolute top-2 right-2 p-2 rounded-md bg-gray-200 dark:bg-[#2a2a2a] text-gray-600 dark:text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-300 dark:hover:bg-[#3a3a3a]"
        title="Copy to clipboard"
      >
        <Copy className="h-4 w-4" />
      </button>
    </div>
  );
}

/**
 * Step indicator component
 */
function Step({
  number,
  title,
  children,
}: {
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-4">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-900 dark:bg-white text-white dark:text-gray-900 flex items-center justify-center text-sm font-bold">
        {number}
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-3">{title}</h4>
        <div className="space-y-3">{children}</div>
      </div>
    </div>
  );
}

/**
 * CLI + SDKs Getting Started page
 */
export function GettingStartedPage() {
  const [selectedSdk, setSelectedSdk] = useState<SdkType>('cli');

  const cliInstructions = {
    commands: `# Authenticate to GitHub Packages:
gh auth login -s read:packages # or gh auth refresh -s read:packages
gh auth token
npm login --registry=https://npm.pkg.github.com --scope=@coreai-microsoft

# Install globally
npm install -g @coreai-microsoft/adc-cli

# Verify installation
adc --version`,
  };

  return (
    <div>
      <PageHeader
        title="CLI + SDKs"
        description="Get started with the ADC command-line interface and client SDKs"
        breadcrumbs={[{ label: 'CLI + SDKs' }]}
      />

      <div className="max-w-4xl space-y-8">
        {/* SDK/CLI Selector */}
        <Card>
          <CardHeader
            title="Choose Your Integration"
            description="Select how you want to interact with ADC"
          />
          <CardBody>
            <div className="flex flex-wrap gap-4">
              <button
                type="button"
                onClick={() => setSelectedSdk('cli')}
                className={clsx(
                  'flex-1 min-w-[200px] p-4 rounded-xl border-2 transition-all',
                  selectedSdk === 'cli'
                    ? 'border-gray-900 dark:border-white bg-gray-50 dark:bg-[#1a1a1a]'
                    : 'border-gray-200 dark:border-[#2a2a2a] hover:border-gray-300 dark:hover:border-[#3a3a3a]'
                )}
              >
                <Terminal className="h-8 w-8 text-gray-700 dark:text-gray-300 mb-3" />
                <h3 className="font-semibold text-gray-900 dark:text-white">CLI</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Command-line interface for terminal workflows
                </p>
              </button>

              <button
                type="button"
                onClick={() => setSelectedSdk('python')}
                className={clsx(
                  'flex-1 min-w-[200px] p-4 rounded-xl border-2 transition-all',
                  selectedSdk === 'python'
                    ? 'border-gray-900 dark:border-white bg-gray-50 dark:bg-[#1a1a1a]'
                    : 'border-gray-200 dark:border-[#2a2a2a] hover:border-gray-300 dark:hover:border-[#3a3a3a]'
                )}
              >
                <Code className="h-8 w-8 text-gray-700 dark:text-gray-300 mb-3" />
                <h3 className="font-semibold text-gray-900 dark:text-white">Python SDK</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Native Python library for scripting and automation
                </p>
              </button>

              <button
                type="button"
                onClick={() => setSelectedSdk('typescript')}
                className={clsx(
                  'flex-1 min-w-[200px] p-4 rounded-xl border-2 transition-all',
                  selectedSdk === 'typescript'
                    ? 'border-gray-900 dark:border-white bg-gray-50 dark:bg-[#1a1a1a]'
                    : 'border-gray-200 dark:border-[#2a2a2a] hover:border-gray-300 dark:hover:border-[#3a3a3a]'
                )}
              >
                <Code className="h-8 w-8 text-gray-700 dark:text-gray-300 mb-3" />
                <h3 className="font-semibold text-gray-900 dark:text-white">TypeScript SDK</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  TypeScript/JavaScript library for Node.js applications
                </p>
              </button>
            </div>
          </CardBody>
        </Card>

        {/* CLI Instructions */}
        {selectedSdk === 'cli' && (
          <>
            {/* Installation Steps */}
            <Card>
              <CardHeader
                title="Installation"
                icon={<Download className="h-5 w-5" />}
                action={
                  <a
                    href={RELEASE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-[#2a2a2a] rounded-lg hover:bg-gray-200 dark:hover:bg-[#3a3a3a] transition-colors"
                  >
                    <ExternalLink className="h-4 w-4" />
                    View Releases
                  </a>
                }
              />
              <CardBody className="space-y-6">
                <Step number={1} title="Install via npm">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Install the ADC CLI globally using npm. Requires Node.js 20+ and npm.
                  </p>
                  <CodeBlock code={cliInstructions.commands} />
                </Step>

                <Step number={2} title="Authenticate with GitHub">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    ADC uses your GitHub account for authentication. Run the login command to
                    authenticate:
                  </p>
                  <CodeBlock code="adc auth login" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    This will open a browser window for GitHub OAuth authentication.
                  </p>
                </Step>

                <Step number={3} title="Create Your First Sandbox">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Create a new sandbox using the public Ubuntu image:
                  </p>
                  <CodeBlock
                    code={`# Create a sandbox with the default Ubuntu image
adc sandbox create --disk ubuntu

# List your sandboxes
adc sandbox list

# SSH into your sandbox
adc shell <sandbox-id>`}
                  />
                </Step>
              </CardBody>
            </Card>

            {/* Quick Reference */}
            <Card>
              <CardHeader title="Quick Reference" />
              <CardBody>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                      Common Commands
                    </h4>
                    <CodeBlock
                      code={`adc sandbox create --name <name>  # Create sandbox
adc sandbox list                   # List sandboxes
adc sandbox delete <id>            # Delete sandbox
adc shell <id>                     # Open interactive shell in sandbox
adc snapshot create <id>           # Create snapshot
adc auth logout                    # Log out`}
                    />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                      Get Help
                    </h4>
                    <CodeBlock
                      code={`adc --help              # Show all commands
adc sandbox --help      # Show sandbox commands
adc <command> --help    # Show command help`}
                    />
                  </div>
                </div>
              </CardBody>
            </Card>
          </>
        )}

        {/* Python SDK Instructions */}
        {selectedSdk === 'python' && (
          <>
            <Card>
              <CardHeader
                title="Installation"
                icon={<Download className="h-5 w-5" />}
                action={
                  <a
                    href={RELEASE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-[#2a2a2a] rounded-lg hover:bg-gray-200 dark:hover:bg-[#3a3a3a] transition-colors"
                  >
                    <ExternalLink className="h-4 w-4" />
                    View Releases
                  </a>
                }
              />
              <CardBody className="space-y-6">
                <Step number={1} title="Install the SDK">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Download the wheel or source distribution from the releases page and install
                    with pip:
                  </p>
                  <CodeBlock
                    code={`# From wheel (download from release assets)
pip install adc_sdk-<version>-py3-none-any.whl

# From source distribution
pip install adc_sdk-<version>.tar.gz`}
                  />
                </Step>

                <Step number={2} title="Create Your First Sandbox">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Here's a complete example to create and interact with a sandbox:
                  </p>
                  <CodeBlock
                    language="python"
                    title="create_sandbox.py"
                    code={`import asyncio
from adc import Adc

async def main():
    # Initialize the client
    async with Adc(api_key="your-api-key") as adc:
        # Create a disk image from a Docker image
        disk_image = await adc.disk_images.create(
            labels={"name": "ubuntu-sandbox"},
            base_image="docker.io/library/ubuntu:latest",
        )

        # Create sandbox from disk image
        sandbox = await adc.sandboxes.create_from_disk_image(
            disk_image=disk_image,
            cpu="1",
            memory="1024Mi",
            labels={"name": "my-sandbox"},
            entrypoint=["/bin/sleep"],
            cmd=["infinity"],
        )

        print(f"Created sandbox: {sandbox.id}")
        print(f"State: {sandbox.state}")

        # Execute a command
        result = await sandbox.execute_shell_command("echo 'Hello!'")
        print(result.stdout)

        # Clean up when done
        # await sandbox.delete()

asyncio.run(main())`}
                  />
                </Step>
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="SDK Features" />
              <CardBody>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        Sandbox Management
                      </h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Create, list, update, and delete sandboxes programmatically
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">File Operations</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Upload, download, and manage files in your sandboxes
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">Snapshots</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Create and restore sandbox snapshots for reproducibility
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">Async Support</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Full async/await support for high-performance applications
                      </p>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </>
        )}

        {/* TypeScript SDK Instructions */}
        {selectedSdk === 'typescript' && (
          <>
            <Card>
              <CardHeader
                title="Installation"
                icon={<Download className="h-5 w-5" />}
                action={
                  <a
                    href={RELEASE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-[#2a2a2a] rounded-lg hover:bg-gray-200 dark:hover:bg-[#3a3a3a] transition-colors"
                  >
                    <ExternalLink className="h-4 w-4" />
                    View Releases
                  </a>
                }
              />
              <CardBody className="space-y-6">
                <Step number={1} title="Install the SDK">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Option 1: Install from GitHub Packages (requires authentication):
                  </p>
                  <CodeBlock
                    code={`# Authenticate to GitHub Packages:
gh auth login -s read:packages # or gh auth refresh -s read:packages
gh auth token
npm login --registry=https://npm.pkg.github.com --scope=@coreai-microsoft

# Install the SDK
npm install @coreai-microsoft/adc-sdk`}
                  />
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                    Option 2: Download the tarball from the releases page and install locally:
                  </p>
                  <CodeBlock code="npm install ./coreai-microsoft-adc-sdk-<version>.tgz" />
                </Step>

                <Step number={2} title="Set Up Authentication">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Configure authentication using an API key or GitHub Device Flow:
                  </p>
                  <CodeBlock
                    code={`// Option 1: Configure with API key
const adc = new Adc({
  apiKey: 'your-api-key',
});

// Option 2: Use GitHub Device Flow
const adc = new Adc({});
await adc.login(); // Opens browser for GitHub authentication`}
                    language="typescript"
                  />
                </Step>

                <Step number={3} title="Create Your First Sandbox">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Here's a complete example to create and interact with a sandbox:
                  </p>
                  <CodeBlock
                    language="typescript"
                    title="create-sandbox.ts"
                    code={`import { Adc } from '@coreai-microsoft/adc-sdk';

async function main() {
  // Initialize the client
  const adc = new Adc({ apiKey: 'your-api-key' });

  // Create a disk image from a Docker image
  const diskImage = await adc.diskImages.create({
    labels: { name: 'ubuntu-sandbox' },
    baseImage: 'docker.io/library/ubuntu:latest',
  });

  // Create sandbox from disk image
  const sandbox = await adc.sandboxes.createFromDiskImage({
    diskImage: diskImage,
    cpu: '1',
    memory: '1024Mi',
    labels: { name: 'my-sandbox' },
    entrypoint: ['/bin/sleep'],
    cmd: ['infinity'],
  });

  console.log(\`Created sandbox: \${sandbox.id}\`);
  console.log(\`State: \${sandbox.state}\`);

  // Execute a command
  const result = await sandbox.executeShellCommand('echo "Hello, World!"');
  console.log(result.stdout);

  // Clean up when done
  // await sandbox.delete();
  adc.close();
}

main().catch(console.error);`}
                  />
                </Step>
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="SDK Features" />
              <CardBody>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        Full TypeScript Support
                      </h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Complete type definitions for excellent IDE support
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        Promise-based API
                      </h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Modern async/await syntax for clean, readable code
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        Sandbox Management
                      </h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Create, list, update, and delete sandboxes programmatically
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">File Operations</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Upload, download, and manage files in your sandboxes
                      </p>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </>
        )}

        {/* Help Section */}
        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">Need Help?</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Check out our documentation or open an issue on GitHub
                </p>
              </div>
              <div className="flex gap-3">
                <a
                  href="https://msft.ghe.com/coreai/adc"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-[#2a2a2a] rounded-lg hover:bg-gray-200 dark:hover:bg-[#3a3a3a] transition-colors"
                >
                  <ExternalLink className="h-4 w-4" />
                  GitHub
                </a>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
